import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { type NovaAuthorizationCapability, useNovaAuthorization } from '@/composables/useNovaAuthorization';
import { usePermissions } from '@/composables/usePermissions';
import { useLDFlag } from 'launchdarkly-vue-client-sdk';
import { FeatureFlags } from '@/plugins/launchDarkly/featureFlags';

declare module 'vue-router' {
    interface RouteMeta {
        navigation?: {
            showInMenu?: boolean;
            permissions?: {
                rbacRoles?: string[];
                ownerRoles?: string[];
                actionPermissions?: string[];
                viewPermissions?: string[];
            };
            requiredCapability?: NovaAuthorizationCapability;
            order?: number;
            parent?: string;
            parentIcon?: string;
            label?: string;
            icon?: string;
            featureFlag?: string;
        };
        title?: string;
    }
}

export function useNavigation() {
    const router = useRouter();
    const { isAuthorized } = useNovaAuthorization();
    const { hasPermission } = usePermissions();
    
    // Feature flag checks
    const featureFlagChecks: Record<string, ReturnType<typeof useLDFlag>> = {
      [FeatureFlags.PAYMENTS_INTEGRATION]: useLDFlag(FeatureFlags.PAYMENTS_INTEGRATION, false),
      [FeatureFlags.NOTIFICATION_SETTINGS]: useLDFlag(FeatureFlags.NOTIFICATION_SETTINGS, false),
      [FeatureFlags.IMPORT_HUB]: useLDFlag(FeatureFlags.IMPORT_HUB, false),
    };
    
    const navigationItems = computed(() => {
        const allRoutes = router.getRoutes();
        
        // First pass: collect all routes for processing
        const routesToProcess = allRoutes
            .filter(route => route.meta?.navigation?.showInMenu)
            .filter(route => {
                const requiredCapability = route.meta.navigation?.requiredCapability;
                return !requiredCapability || isAuthorized(requiredCapability);
            })
            .filter(route => {
                // Skip permission check for parent routes (they have children property)
                const isParentRoute = route.children && route.children.length > 0;
                if (isParentRoute) return true;
                
                // Apply permission check for child routes
                return hasPermission(route.meta.navigation?.permissions);
            })
            .filter(route => {
                const featureFlag = route.meta.navigation?.featureFlag;
                if (featureFlag && featureFlagChecks[featureFlag]) {
                    return featureFlagChecks[featureFlag].value;
                }
                return true;
            })
            .sort((a, b) =>
                (a.meta.navigation?.order || 0) - (b.meta.navigation?.order || 0)
            );
            
        return routesToProcess
            .reduce((acc, route) => {
                const parent = route.meta.navigation?.parent;

                if (parent) {
                    // Group by parent for hierarchical menu
                    const parentKey = String(parent);
                    if (!acc[parentKey]) {
                        acc[parentKey] = {
                            name: parent,
                            icon: route.meta.navigation?.parentIcon,
                            children: [],
                            order: 999, // High default, will be updated when parent route is processed
                            rbacRole: [],
                            ownerRole: [],
                            actionPermissions: [],
                            viewPermissions: []
                        };
                    }
                    acc[parentKey].children.push({
                        name: route.name,
                        label: route.meta.navigation?.label || route.meta.title,
                        path: route.path,
                        icon: route.meta.navigation?.icon,
                        featureFlag: route.meta.navigation?.featureFlag,
                        rbacRole: route.meta.navigation?.permissions?.rbacRoles || [],
                        ownerRole: route.meta.navigation?.permissions?.ownerRoles || [],
                        actionPermissions: route.meta.navigation?.permissions?.actionPermissions || [],
                        viewPermissions: route.meta.navigation?.permissions?.viewPermissions || []
                    });
                } else {
                    // Standalone top-level item or parent route itself
                    const itemName = String(route.meta.navigation?.label || route.meta.title || route.name || 'Unknown');
                    if (!acc[itemName]) {
                        acc[itemName] = {
                            name: itemName,
                            icon: route.meta.navigation?.icon,
                            path: route.path,
                            children: [], // Empty children for standalone items
                            order: route.meta.navigation?.order || 999,
                            rbacRole: route.meta.navigation?.permissions?.rbacRoles || [],
                            ownerRole: route.meta.navigation?.permissions?.ownerRoles || [],
                            actionPermissions: route.meta.navigation?.permissions?.actionPermissions || [],
                            viewPermissions: route.meta.navigation?.permissions?.viewPermissions || []
                        };
                    } else {
                        // Update existing item (parent route processed after children)
                        if (route.path) acc[itemName].path = route.path;
                        if (route.meta.navigation?.icon) acc[itemName].icon = route.meta.navigation?.icon;
                        if (route.meta.navigation?.order !== undefined) acc[itemName].order = route.meta.navigation?.order;
                        // Merge permissions if parent route has them
                        if (route.meta.navigation?.permissions?.rbacRoles?.length) {
                            acc[itemName].rbacRole = route.meta.navigation.permissions.rbacRoles;
                        }
                        if (route.meta.navigation?.permissions?.ownerRoles?.length) {
                            acc[itemName].ownerRole = route.meta.navigation.permissions.ownerRoles;
                        }
                        // Keep existing children
                    }
                }

                return acc;
            }, {} as Record<string, any>);
    });
    
    const navigationArray = computed(() => {
        return Object.values(navigationItems.value).sort((a, b) => (a.order || 999) - (b.order || 999));
    });
    
    return { navigationItems, navigationArray };
}
